import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import EmployeeAuthModal from "../../employeeAuth/EmployeeAuthModal";
import { toast } from "react-toastify";
import { ChevronDown } from "lucide-react";
import DatePicker from "react-datepicker";
import DetailedTicketView from "./DetailedTicketView";
import "react-datepicker/dist/react-datepicker.css";

const ManageTickets = ({ isSidebarAdmin }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [ticketData, setTicketData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [searchNameEmail, setSearchNameEmail] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [searchTicketId, setSearchTicketId] = useState("");
  const [searchAWBNumber, setSearchAWBNumber] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [roleData, setRoleData] = useState([]);
  const [showEmployeeAuthModal, setShowEmployeeAuthModal] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [platformDropdownOpen, setPlatformDropdownOpen] = useState(false);
  const [showDetailedView, setShowDetailedView] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const [employeeAccess, setEmployeeAccess] = useState({
    canView: false,
    canUpdate: false,
  });

  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);

  const statusOptions = [
    { label: "All", value: "" },
    { label: "Active", value: "active" },
    { label: "Resolved", value: "resolved" },
    { label: "Deleted", value: "deleted" },
  ];

  const categoryOptions = [
    { label: "Select Category", value: "" },
    { label: "Shipment Dispute", value: "Shipment Dispute" },
    { label: "Finance", value: "Finance" },
    { label: "Pickup & Delivery", value: "Pickup & Delivery" },
    { label: "Shipment NDR & RTO", value: "Shipment NDR & RTO" },
    { label: "KYC & Bank Verification", value: "KYC & Bank Verification" },
    { label: "Technical Support", value: "Technical Support" },
    { label: "Billing & Taxation", value: "Billing & Taxation" },
    { label: "Claims", value: "Claims" },
    { label: "Others", value: "Others" },
  ];

  const platformOptions = [
    { label: "Select Platform", value: "" },
    { label: "User", value: "user" },
    { label: "Admin", value: "admin" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isSidebarAdmin) {
          setEmployeeAccess({ canView: true, canUpdate: true });
          setShowEmployeeAuthModal(false);
        } else {
          const [session, token] = document.cookie.split("=");
          const roleRes = await axios.get(
            `${REACT_APP_BACKEND_URL}/staffRole/verify`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const support = roleRes.data.employee;
          console.log("Support Access Rights:", support);
          const canView =
            !!support?.accessRights?.support?.["Manage ticket"]?.view;
          const canUpdate =
            !!support?.accessRights?.support?.["Manage ticket"]?.action;
          // console.log(canUpdate);
          setRoleData(support);
          setEmployeeAccess({ canView, canUpdate });
          setShowEmployeeAuthModal(!canView);
          if (!canView) return;
        }

        // Fetch tickets
        const [session, token] = document.cookie.split("=");
        const headers = { authorization: `Bearer ${token}` };
        const response = await axios.get(`${REACT_APP_BACKEND_URL}/support`, {
          headers,
        });
        setTicketData(response.data || []);
        console.log("Ticket Data:", response.data);
        setFilteredData(response.data || []);
      } catch (error) {
        console.error("Error fetching tickets or role:", error);
        if (!isSidebarAdmin) setShowEmployeeAuthModal(true);
      }
    };
    fetchData();
  }, [isSidebarAdmin, REACT_APP_BACKEND_URL]);

  useEffect(() => {
    // Filter data based on various criteria
    let filtered = ticketData.filter((ticket) =>
      (ticket.fullname + ticket.email)
        .toLowerCase()
        .includes(searchNameEmail.toLowerCase())
    );

   

    if (searchDate) {
      filtered = filtered.filter(
        (ticket) =>
          new Date(ticket.createdAt).toLocaleDateString() ===
          new Date(searchDate).toLocaleDateString()
      );
    }

    if (searchTicketId) {
      filtered = filtered.filter((ticket) =>
        ticket.ticketNumber.toLowerCase().includes(searchTicketId.toLowerCase())
      );
    }

    if (searchAWBNumber) {
      filtered = filtered.filter((ticket) =>
        ticket.awbNumbers?.includes(searchAWBNumber)
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(
        (ticket) => ticket.category === selectedCategory
      );
    }

    if (selectedPlatform) {
      filtered = filtered.filter((ticket) =>
        selectedPlatform === "admin" ? ticket.isAdmin : !ticket.isAdmin
      );
    }

    if (selectedStatus) {
      filtered = filtered.filter((ticket) =>
        ticket.status?.toLowerCase().includes(selectedStatus.toLowerCase())
      );
    }

    setFilteredData(filtered);
  }, [
    ticketData,
    searchNameEmail,
    searchDate,
    searchTicketId,
    searchAWBNumber,
    selectedCategory,
    selectedPlatform,
    selectedStatus,
  ]);

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      console.log("Handling status change:", ticketId, newStatus);

      const [session, token] = document.cookie.split("=");

      if (!token) {
        alert("Session expired. Please log in again.");
        return;
      }

      const headers = {
        authorization: `Bearer ${token}`,
      };

      const hasUpdateAccess =
        roleData.accessRights?.support?.["Manage ticket"]?.update;
      const isEmployeeActive = roleData.isEmpActive === true;

      if ((hasUpdateAccess && isEmployeeActive) || isSidebarAdmin) {
        console.log("Authorized to update");

        const response = await axios.put(
          `${REACT_APP_BACKEND_URL}/support/${ticketId}/status`,
          { status: newStatus },
          { headers }
        );

        if (response.status === 200) {
          setTicketData((prevData) =>
            prevData.map((ticket) =>
              ticket._id === ticketId
                ? { ...ticket, status: newStatus }
                : ticket
            )
          );

          toast.success(
            newStatus === "resolved"
              ? "Ticket marked as resolved!"
              : "Ticket marked as deleted!"
          );
        } else {
          console.log("Failed to update status:", response.data.message);
          toast.error("Failed to update ticket status. Try again.");
        }
      } else {
        console.log(
          "Unauthorized: either update access or employee status is false."
        );
        alert("Unauthorized to update ticket status.");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.warning("failed to update ticket status.");
    }
  };

  const filterByStatus = (status) => {
    setSelectedStatus(status);
  };

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedData = filteredData.slice(
    startIndex,
    startIndex + itemsPerPage
  );
  const dropdownRef = useRef(null); // Reference for dropdown

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isDropdownButton = event.target.closest(".dropdown-button");
      const isDropdownMenu = event.target.closest(".dropdown-menu");
      if (!isDropdownButton && !isDropdownMenu) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleViewDetails = (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailedView(true);
    setOpenDropdown(null);
  };

  if (!isSidebarAdmin && showEmployeeAuthModal) {
    return (
      <EmployeeAuthModal
        employeeModalShow={true}
        employeeModalClose={() => setShowEmployeeAuthModal(false)}
      />
    );
  }

// In ManageTickets.jsx
if (showDetailedView && selectedTicket) {
  return (
    <DetailedTicketView
      ticket={selectedTicket}
      onClose={() => setShowDetailedView(false)}
      onStatusChange={handleStatusChange} // <-- Pass this!
      isSidebarAdmin={isSidebarAdmin}
      employeeAccess={employeeAccess}
      roleData={roleData}
    />
  );
}
  return (
    <div className="md:p-4">
      {/* Status Filter - Responsive */}
      <div className="mb-10">
        {/* Desktop View: Buttons */}
        <div className="hidden md:flex space-x-2">
          <button
            className={`px-4 py-1 rounded-lg ${
              selectedStatus === "active" ? "bg-[#0CBB7D] text-white" : ""
            }`}
            onClick={() => filterByStatus("active")}
          >
            Active
          </button>
          <button
            className={`px-4 py-1 rounded-lg ${
              selectedStatus === "resolved" ? "bg-[#0CBB7D] text-white" : ""
            }`}
            onClick={() => filterByStatus("resolved")}
          >
            Resolved
          </button>
          <button
            className={`px-4 py-1 rounded-lg ${
              selectedStatus === "deleted" ? "bg-[#0CBB7D] text-white" : ""
            }`}
            onClick={() => filterByStatus("deleted")}
          >
            Deleted
          </button>
          <button
            className={`px-6 py-1 rounded-lg ${
              selectedStatus === "" ? "bg-[#0CBB7D] text-white" : ""
            }`}
            onClick={() => filterByStatus("")}
          >
            All
          </button>
        </div>

        {/* Mobile View: Custom Dropdown */}
        <div className="md:hidden relative w-full">
          <button
            className="w-full p-2 rounded-lg border bg-[#0CBB7D] text-white font-medium flex justify-between items-center"
            onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
          >
            {statusOptions.find((o) => o.value === selectedStatus)?.label ||
              "Select Status"}
            <ChevronDown className="w-4 h-4 text-white" />
          </button>

          {statusDropdownOpen && (
            <ul className="absolute z-50 mt-1 w-full bg-white rounded-lg border shadow-md max-h-48 overflow-auto">
              {statusOptions.map((option) => (
                <li
                  key={option.value}
                  className={`cursor-pointer px-4 py-2 text-gray-700 hover:bg-green-50 ${
                    selectedStatus === option.value
                      ? "font-semibold bg-green-50"
                      : ""
                  }`}
                  onClick={() => {
                    filterByStatus(option.value);
                    setStatusDropdownOpen(false);
                  }}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Filters and Search Inputs */}
      <div className="grid md:grid-cols-6 grid-cols-1 gap-2 mb-10">
        <input
          type="text"
          placeholder="Search By Name, Email"
          className="border px-3 py-1.5 rounded-lg text-sm placeholder:text-[12px] font-[600] h-8 md:h-9 focus:outline-none"
          value={searchNameEmail}
          onChange={(e) => setSearchNameEmail(e.target.value)}
        />
        <div className="relative w-full">
  <button
    onClick={() => setDateDropdownOpen(!dateDropdownOpen)}
    className="w-full border px-3 py-1.5 rounded-lg text-[12px] font-[600] flex justify-between items-center h-8 md:h-9 transition-all duration-200 text-gray-400 bg-white"
  >
    {searchDate
      ? new Date(searchDate).toLocaleDateString()
      : "Select Date"}
    <ChevronDown
      className={`w-4 h-4 ml-2 transition-transform duration-200 ${
        dateDropdownOpen ? "rotate-180" : ""
      }`}
    />
  </button>

  <div
    className={`absolute left-0 mt-1 w-full z-30 transition-all duration-200 transform origin-top ${
      dateDropdownOpen
        ? "scale-y-100 opacity-100"
        : "scale-y-0 opacity-0 pointer-events-none"
    }`}
  >
    <div className="w-full max-w-xs mx-auto">
      <DatePicker
        selected={searchDate ? new Date(searchDate) : null}
        onChange={(date) => {
          setSearchDate(date?.toISOString().slice(0, 10));
          setDateDropdownOpen(false);
        }}
        inline
        className="w-full"
      />
    </div>
  </div>
</div>


        <input
          type="text"
          placeholder="Search By Ticket ID"
          className="border px-3 py-1.5 rounded-lg text-[12px] font-[600] placeholder:text-[12px] font-[600] h-8 md:h-9 focus:outline-none"
          value={searchTicketId}
          onChange={(e) => setSearchTicketId(e.target.value)}
        />
        <input
          type="text"
          placeholder="Search By AWB number"
          className="border px-3 py-1.5 rounded-lg text-[12px] font-[600] placeholder:text-[12px] font-[600] h-8 md:h-9 focus:outline-none"
          value={searchAWBNumber}
          onChange={(e) => setSearchAWBNumber(e.target.value)}
        />

        {/* Category Dropdown */}
        <div className="relative dropdown-container h-8 md:h-9">
          <button
            className="w-full h-full border px-3 py-1.5 rounded-lg text-[12px] font-[600] flex justify-between items-center bg-white text-gray-400"
            onClick={() =>
              setCategoryDropdownOpen((prev) => ({
                ...prev,
                category: !prev.category,
                platform: false,
              }))
            }
          >
            {categoryOptions.find((o) => o.value === selectedCategory)?.label ||
              "Select Category"}
            <ChevronDown className="w-4 h-4" />
          </button>
          {categoryDropdownOpen.category && (
            <ul className="absolute z-50 mt-1 w-full bg-white rounded-lg border shadow-md max-h-60 overflow-auto text-[12px]">
              {categoryOptions.map((option) => (
                <li
                  key={option.value}
                  className={`cursor-pointer px-4 py-2 hover:bg-green-50 ${
                    selectedCategory === option.value
                      ? "font-semibold bg-green-50"
                      : ""
                  }`}
                  onClick={() => {
                    setSelectedCategory(option.value);
                    setCategoryDropdownOpen((prev) => ({
                      ...prev,
                      category: false,
                    }));
                  }}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Platform Dropdown */}
        <div className="relative dropdown-container h-8 md:h-9">
          <button
            className="w-full h-full border px-3 py-1.5 rounded-lg text-[12px] font-[600] flex justify-between items-center bg-white text-gray-400"
            onClick={() =>
              setPlatformDropdownOpen((prev) => ({
                ...prev,
                platform: !prev.platform,
                category: false,
              }))
            }
          >
            {platformOptions.find((o) => o.value === selectedPlatform)?.label ||
              "Select Platform"}
            <ChevronDown className="w-4 h-4" />
          </button>
          {platformDropdownOpen.platform && (
            <ul className="absolute z-50 mt-1 w-full bg-white rounded-lg border shadow-md max-h-60 overflow-auto text-[12px]">
              {platformOptions.map((option) => (
                <li
                  key={option.value}
                  className={`cursor-pointer px-4 py-2 hover:bg-green-50 ${
                    selectedPlatform === option.value
                      ? "font-semibold bg-green-50"
                      : ""
                  }`}
                  onClick={() => {
                    setSelectedPlatform(option.value);
                    setPlatformDropdownOpen((prev) => ({
                      ...prev,
                      platform: false,
                    }));
                  }}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Ticket Table */}
      <div className="w-full">
        {/* Desktop View */}
        <div className="hidden md:block">
          <table className="w-full border border-gray-300">
            <thead>
              <tr className="bg-green-50 text-[14px]">
                <th className="p-2 border">Sl No.</th>
                <th className="p-2 border">Ticket ID</th>
                <th className="p-2 border">AWB No.</th>
                <th className="p-2 border">Name</th>
                <th className="p-2 border">Category</th>
                <th className="p-2 border">Subcategory</th>
                <th className="p-2 border">Status</th>
                
                <th className="p-2 border">Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedData.length > 0 ? (
                displayedData.map((ticket, index) => {
                  const awbNumbers = ticket.awbNumbers || [];
                  const displayedAWB = awbNumbers.slice(0, 2).join(", ");
                  const hasMore = awbNumbers.length > 2;

                  return (
                    <tr
                      key={ticket._id}
                      className="text-center text-[12px] border"
                    >
                      <td className="p-2 border">{startIndex + index + 1}</td>
                      <td className="p-2 border">{ticket.ticketNumber}</td>
                      <td className="p-2 border relative group">
                        <span>
                          {displayedAWB}
                          {hasMore && " ..."}
                        </span>
                        {hasMore && (
                          <div className="absolute left-0 bottom-full mb-2 w-max bg-gray-700 text-white text-sm p-2 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                            {awbNumbers.join(", ")}
                          </div>
                        )}
                      </td>
                      <td className="p-2 border">
                        {ticket.fullname}
                      </td>
                      <td className="p-2 border">{ticket.category}</td>
                      <td className="p-2 border">{ticket.subcategory}</td>
                      <td className="p-2 border">
                        {ticket.status || "Pending"}
                      </td>
                      
                      <td className="p-2 border relative" ref={dropdownRef}>
                        <button
                          className={`dropdown-button bg-[#0CBB7D] text-white px-2 text-[14px] rounded ${
                            !isSidebarAdmin && !employeeAccess.canUpdate
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          disabled={
                            !isSidebarAdmin && !employeeAccess.canUpdate
                          }
                          onClick={() => {
                            if (isSidebarAdmin || employeeAccess.canUpdate) {
                              setOpenDropdown(
                                openDropdown === ticket._id ? null : ticket._id
                              );
                            }
                          }}
                        >
                          View
                        </button>

                        {openDropdown === ticket._id && (
                          <div className="dropdown-menu absolute right-0 mt-1 w-28 bg-white border rounded shadow-md z-50">
                             <button
        className="block w-full text-gray-500 py-[6px] text-sm hover:bg-gray-200 active:bg-green-100"
        onMouseDown={() => handleViewDetails(ticket)}
      >
        View Details
      </button>
                            <button
                              className="block w-full text-green-600 py-[6px] hover:bg-gray-100"
                              onClick={() => {
                                handleStatusChange(ticket._id, "resolved");
                                setOpenDropdown(null);
                              }}
                            >
                              Resolved
                            </button>
                            <button
                              className="block w-full text-red-600 py-[6px] hover:bg-gray-100"
                              onClick={() => {
                                handleStatusChange(ticket._id, "deleted");
                                setOpenDropdown(null);
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="text-center p-4">
                    No data available in table
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile View (Card Structure) */}
        <div className="md:hidden space-y-4">
          {displayedData.length > 0 ? (
            displayedData.map((ticket, index) => {
              const awbNumbers = ticket.awbNumbers || [];
              const displayedAWB = awbNumbers.slice(0, 2).join(", ");
              const hasMore = awbNumbers.length > 2;

              return (
                <div
                  key={ticket._id}
                  className="border p-4 rounded-lg shadow-md bg-green-50"
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-base font-semibold">
                      Ticket ID: {ticket.ticketNumber}
                    </h3>
                    <button
                      className={`dropdown-button bg-[#0CBB7D] text-white px-2 text-[14px] rounded ${
                        !isSidebarAdmin && !employeeAccess.canUpdate
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      disabled={!isSidebarAdmin && !employeeAccess.canUpdate}
                      onClick={() => {
                        if (isSidebarAdmin || employeeAccess.canUpdate) {
                          setOpenDropdown(
                            openDropdown === ticket._id ? null : ticket._id
                          );
                        }
                      }}
                    >
                      View
                    </button>
                  </div>
                  <div className="mt-2 text-sm">
                    <p>
                      <strong>AWB No.:</strong> {displayedAWB}
                      {hasMore && " ..."}
                    </p>
                    <p>
                      <strong>Category:</strong> {ticket.category}
                    </p>
                    <p>
                      <strong>Subcategory:</strong> {ticket.subcategory}
                    </p>
                    <p>
                      <strong>Status:</strong> {ticket.status || "Pending"}
                    </p>
                    <p>
                      <strong>Created On:</strong>{" "}
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  {openDropdown === ticket._id && (
                    <div className="mt-2 w-full bg-gray-100 border rounded shadow-md p-2">
                     <button
        className="block w-full text-gray-500 py-[6px] hover:bg-gray-100"
        onMouseDown={() => handleViewDetails(ticket)}
      >
        View Details
      </button>
                      <button
                        className="block w-full text-green-600 py-2 text-sm hover:bg-gray-200 active:bg-green-100"
                        onMouseDown={() => {
                          handleStatusChange(ticket._id, "resolved");
                          setOpenDropdown(null);
                        }}
                      >
                        Resolved
                      </button>

                      <button
                        className="block w-full text-red-600 py-2 text-sm hover:bg-gray-200 active:bg-red-100"
                        onMouseDown={() => {
                          handleStatusChange(ticket._id, "deleted");
                          setOpenDropdown(null);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center p-4 border rounded-lg bg-green-50 text-sm shadow-md">
              No data available in table
            </div>
          )}
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="flex justify-between items-center mt-4">
        <button
          className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50"
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ManageTickets;
